/**
 * ══════════════════════════════════════════════════════════════════════════
 * LA COMISIÓN DE LOGÍSTICA — EL LADO QUE LO PROMETE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ⚠ ESPEJO. El original es `functions/src/comisionLogistica.ts`, que es el que
 * COBRA. Aquí solo se PROMETE: esto es lo que el proveedor ve en pantalla antes
 * de aceptar un trabajo.
 *
 * No se edita este archivo: se edita el del servidor y se copia su regla —todo
 * lo que va después de la línea de marca, igual— y se corre:
 *
 *     node scripts/probar-espejos.mjs
 *
 * El motivo de que exista el espejo y no un import es que `functions/` y `src/`
 * se compilan aparte y no se pueden importar entre sí. El motivo de que exista
 * la PRUEBA es que ya se separaron dos veces: la pantalla decía RD$1,000 de bono
 * de referidos y el servidor pagaba RD$800.
 *
 * El porqué de la tabla —los tramos marginales, el techo de RD$1,200 y por qué
 * la tarjeta se cobra agrupado y no servicio por servicio— está explicado en la
 * cabecera del original. Ahí, no aquí, para que no haya dos versiones del
 * razonamiento.
 */

// @@REGLA-COMPARTIDA@@  de aquí abajo, idéntico en las dos copias.

/** Un tramo de la tabla: `hasta` es el techo de la franja, en pesos. */
export interface TramoDeComision {
  /** Techo de la franja. `null` = de aquí en adelante. */
  hasta: number | null;
  /** Lo que se cobra SOBRE LA PARTE del monto que cae en esta franja. */
  porciento: number;
}

/**
 * La tabla. Marginal: cada tramo cobra solo sobre su parte.
 * Cambiar esto cambia lo que se le cobra a la gente — es dinero, no estilo.
 */
export const TRAMOS_DE_COMISION: TramoDeComision[] = [
  { hasta: 1000, porciento: 10 },
  { hasta: 5000, porciento: 8 },
  { hasta: 15000, porciento: 6 },
  { hasta: null, porciento: 4 },
];

/** Por debajo de esto no baja, para que un mandado de RD$150 deje algo. */
export const COMISION_MINIMA = 25;

/**
 * Y de aquí no sube. Es el freno contra el incentivo a cerrar el trato por
 * fuera: en una mudanza de RD$30,000 la comisión efectiva queda en 4%.
 */
export const COMISION_MAXIMA = 1200;

/** A partir de este acumulado se intenta el cobro agrupado (ver la cabecera). */
export const COBRO_AGRUPADO_DESDE = 500;

/**
 * ══ EL TOPE DE SALDO ═══════════════════════════════════════════════
 *
 * Cuánta comisión puede deber alguien antes de que se le pare de aceptar
 * trabajos NUEVOS. Nunca se le para uno en curso: eso lo decidió el fundador y
 * no se toca.
 *
 * Es lo que sustituye a pedir una tarjeta guardada, y hace el mismo trabajo sin
 * pedirle nada a nadie: un recén llegado no puede acumular mucho antes de
 * demostrar que paga, y el que paga a tiempo gana margen — que es un premio que
 * no nos cuesta un peso.
 *
 * El tope sube con los SERVICIOS ENTREGADOS, que es un dato que el servidor
 * escribe y nadie puede falsificar (`serviciosHechos`). Con la comisión media
 * en unos RD$300, el tope de entrada son dos trabajos: salda tras el primero o
 * el segundo y a partir de ahí se mueve con holgura.
 */
export interface EscalonDeTope {
  /** Servicios entregados a partir de los cuales aplica este tope. */
  desdeServicios: number;
  /** Pesos que puede deber. */
  tope: number;
}

export const TOPES_DE_SALDO: EscalonDeTope[] = [
  { desdeServicios: 0, tope: 500 },
  { desdeServicios: 5, tope: 1000 },
  { desdeServicios: 20, tope: 2000 },
];

/** El tope de quien lleva `serviciosHechos` entregas. */
export function topeDeSaldo(serviciosHechos: unknown): number {
  const hechos = Math.max(0, Math.floor(Number(serviciosHechos) || 0));
  let tope = TOPES_DE_SALDO[0].tope;
  for (const e of TOPES_DE_SALDO) {
    if (hechos >= e.desdeServicios) tope = e.tope;
  }
  return tope;
}

/** Y si no llega, igual se cobra el lunes. 1 = lunes, como `getUTCDay()`. */
export const DIA_DE_COBRO_SEMANAL = 1;

/**
 * Lo que se le cobra al proveedor por un servicio de este monto, en pesos
 * enteros. Redondea hacia abajo: ante la duda, a favor de quien trabaja.
 *
 * Devuelve 0 para un monto que no es un número o que no es positivo — un
 * servicio sin precio acordado todavía no devenga nada.
 */
export function comisionDe(montoAcordado: number): number {
  const monto = Number(montoAcordado);
  if (!Number.isFinite(monto) || monto <= 0) return 0;

  let comision = 0;
  let base = 0;
  for (const tramo of TRAMOS_DE_COMISION) {
    const techo = tramo.hasta === null ? monto : Math.min(tramo.hasta, monto);
    if (techo <= base) continue;
    comision += ((techo - base) * tramo.porciento) / 100;
    base = techo;
    if (base >= monto) break;
  }

  const redondeada = Math.floor(comision);
  if (redondeada > COMISION_MAXIMA) return COMISION_MAXIMA;
  if (redondeada < COMISION_MINIMA) return Math.min(COMISION_MINIMA, Math.floor(monto));
  return redondeada;
}

/**
 * Lo que se le enseña al proveedor ANTES de aceptar. Dos números y nada de
 * letra pequeña: lo que le queda es lo que importa.
 */
export function desgloseDeComision(montoAcordado: number): {
  monto: number;
  comision: number;
  recibe: number;
  porcientoEfectivo: number;
} {
  const monto = Math.max(0, Math.floor(Number(montoAcordado) || 0));
  const comision = comisionDe(monto);
  return {
    monto,
    comision,
    recibe: monto - comision,
    porcientoEfectivo: monto > 0 ? Math.round((comision / monto) * 1000) / 10 : 0,
  };
}
